/**
 * Tile grid math (pure, no Babylon): tiles.json parsing, level selection by device mpp, visible
 * tiles for a rotated orthographic view, the one-tile prefetch ring, texel addressing and a CPU
 * reference of the shader's sampling rules (tiles.json `sampling`).
 *
 * Grid (tiles.json `axes`): tile (x, y) of level z covers world [x0, x0 + tile_m) × [y0, y0 + tile_m)
 * with x0 = ox + x·tile_m, y0 = oy + y·tile_m; y = 0 is the southmost row (TMS). Texel (i, j) =
 * floor(uv · tile_px) with j counted from the SOUTH edge (texture uploaded with flipY).
 */

export interface TileLevelSpec {
  z: number;
  /** metres per texel */
  mpp: number;
  nx: number;
  ny: number;
  tile_m: number;
}

export interface TileGridSpec {
  origin: [number, number];
  extent_m: [number, number];
  tile_px: number;
  /** ordered coarse → fine (z0 first) */
  levels: TileLevelSpec[];
  /** e.g. "tiles/{grade}/{z}/{x}/{y}.webp", relative to the bundle base */
  path: string;
  grades: string[];
}

function num(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new Error(`tiles.json: ${what} is not a finite number`);
  return v;
}
function rec(v: unknown, what: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null) throw new Error(`tiles.json: ${what} is not an object`);
  return v as Record<string, unknown>;
}

/** Validate tiles.json (only the fields the runtime uses). */
export function parseTileGrid(json: unknown): TileGridSpec {
  const t = rec(json, 'root');
  const origin = t.origin as unknown[];
  const extent = t.extent_m as unknown[];
  if (!Array.isArray(origin) || !Array.isArray(extent)) throw new Error('tiles.json: origin/extent_m missing');
  const tilePx = num(t.tile_px, 'tile_px');
  const levelsRaw = t.levels as unknown[];
  if (!Array.isArray(levelsRaw) || levelsRaw.length === 0) throw new Error('tiles.json: levels missing');
  const levels = levelsRaw.map((l, i) => {
    const r = rec(l, `levels[${String(i)}]`);
    const mpp = num(r.mpp, 'level.mpp');
    return {
      z: num(r.z, 'level.z'),
      mpp,
      nx: num(r.nx, 'level.nx'),
      ny: num(r.ny, 'level.ny'),
      tile_m: typeof r.tile_m === 'number' ? r.tile_m : mpp * tilePx,
    };
  });
  levels.sort((a, b) => a.z - b.z);
  return {
    origin: [num(origin[0], 'origin[0]'), num(origin[1], 'origin[1]')],
    extent_m: [num(extent[0], 'extent_m[0]'), num(extent[1], 'extent_m[1]')],
    tile_px: tilePx,
    levels,
    path: typeof t.path === 'string' ? t.path : 'tiles/{grade}/{z}/{x}/{y}.webp',
    grades: Array.isArray(t.grades) ? (t.grades as unknown[]).filter((g): g is string => typeof g === 'string') : [],
  };
}

// ───────────────────────────── keys ─────────────────────────────
const KX = 4096;
/** Integer tile key (z < 128, x, y < 4096): fits an Int32. */
export function tileKey(z: number, x: number, y: number): number {
  return (z * KX + x) * KX + y;
}
export function keyZ(k: number): number {
  return Math.floor(k / (KX * KX));
}
export function keyX(k: number): number {
  return Math.floor(k / KX) % KX;
}
export function keyY(k: number): number {
  return k % KX;
}
/** Ancestor key at level zp ≤ z (the origin is a tile corner at every level, tile_m halves per level). */
export function ancestorKey(k: number, zp: number): number {
  const z = keyZ(k);
  const s = z - zp;
  return tileKey(zp, keyX(k) >> s, keyY(k) >> s);
}

export function tileUrl(grid: TileGridSpec, base: string, grade: string, z: number, x: number, y: number): string {
  const rel = grid.path
    .replace('{grade}', grade)
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y));
  return base.endsWith('/') || base === '' ? base + rel : `${base}/${rel}`;
}

// ───────────────────────────── level selection (09 §6) ─────────────────────────────

/**
 * Level index for a sampling mpp: the level with the largest mpp ≤ `mpp` (z4 magnified beyond,
 * the coarsest level when `mpp` is coarser than every level). `bias` > 0 picks coarser levels
 * (interim 4K fallback, 09 §12).
 */
export function selectLevel(grid: TileGridSpec, mpp: number, bias = 0): number {
  const L = grid.levels; // coarse → fine (mpp descending)
  const finest = L.length - 1;
  let idx = finest; // finer than every level: magnify the finest
  for (let i = 0; i < L.length; i++) {
    const lv = L[i];
    if (lv && lv.mpp <= mpp * (1 + 1e-9)) {
      idx = i; // the first (coarsest) level that is ≤ mpp = the largest such mpp
      break;
    }
  }
  return Math.max(0, Math.min(finest, idx - bias));
}

/**
 * Level for a camera. `levelBy: 'backbuffer'` (default) samples by backbuffer mpp = stage mpp /
 * `pxScale`, where `pxScale` = backbuffer px per stage px (uPxScale = k / r, 09 §6): with the
 * render-scale governor at r > 1 the GL picture has fewer px, so coarser levels suffice.
 * 'stage' uses stage mpp (pixel-art grades at 4K are then exactly the reference ×2, 09 §12).
 * 'device' is an alias of 'backbuffer' (pass k as `pxScale` for device mpp).
 */
export function levelForView(
  grid: TileGridSpec,
  stageMpp: number,
  pxScale: number,
  levelBy: 'backbuffer' | 'device' | 'stage' = 'backbuffer',
  bias = 0,
): number {
  return selectLevel(grid, levelBy === 'stage' ? stageMpp : stageMpp / pxScale, bias);
}

// ───────────────────────────── addressing ─────────────────────────────

export interface TexelAddress {
  x: number;
  y: number;
  i: number;
  j: number;
  /** uv within the tile */
  u: number;
  v: number;
}

/** World point → tile and texel at level index `li` (j from the tile's south edge). */
export function texelAddress(grid: TileGridSpec, li: number, wx: number, wy: number, out: TexelAddress): TexelAddress {
  const lv = grid.levels[li];
  if (!lv) throw new Error(`no level ${String(li)}`);
  const fx = (wx - grid.origin[0]) / lv.tile_m;
  const fy = (wy - grid.origin[1]) / lv.tile_m;
  out.x = Math.floor(fx);
  out.y = Math.floor(fy);
  out.u = fx - out.x;
  out.v = fy - out.y;
  const n = grid.tile_px;
  out.i = Math.min(n - 1, Math.max(0, Math.floor(out.u * n)));
  out.j = Math.min(n - 1, Math.max(0, Math.floor(out.v * n)));
  return out;
}

/**
 * CPU reference of the tile shader's sampling (tiles.json `sampling`), for tests and probes.
 * `texels` is RGBA8, row 0 = SOUTH edge (as uploaded), `n` texels square. Returns
 * [R (coverage-weighted bilinear), G (nearest), B (coverage-weighted bilinear), A (bilinear)].
 */
export function sampleTileReference(texels: Uint8Array, n: number, u: number, v: number, out: Float64Array | number[]): void {
  const at = (i: number, j: number, c: number): number => texels[(j * n + i) * 4 + c] ?? 0;
  const clampI = (i: number): number => (i < 0 ? 0 : i > n - 1 ? n - 1 : i);
  // nearest (G)
  const ni = clampI(Math.floor(u * n));
  const nj = clampI(Math.floor(v * n));
  // bilinear taps
  const tx = u * n - 0.5;
  const ty = v * n - 0.5;
  const i0 = Math.floor(tx);
  const j0 = Math.floor(ty);
  const fx = tx - i0;
  const fy = ty - j0;
  let a = 0;
  let wa = 0;
  let r = 0;
  let b = 0;
  for (let dj = 0; dj < 2; dj++) {
    for (let di = 0; di < 2; di++) {
      const w = (di ? fx : 1 - fx) * (dj ? fy : 1 - fy);
      const ii = clampI(i0 + di);
      const jj = clampI(j0 + dj);
      const A = at(ii, jj, 3);
      a += w * A;
      wa += w * A;
      r += w * A * at(ii, jj, 0);
      b += w * A * at(ii, jj, 2);
    }
  }
  out[0] = wa > 0 ? r / wa : 0;
  out[1] = at(ni, nj, 1);
  out[2] = wa > 0 ? b / wa : 0;
  out[3] = a;
}

// ───────────────────────────── visibility ─────────────────────────────

/** A fixed-capacity set of tile keys (allocation-free reuse per frame). */
export class TileKeySet {
  readonly keys: Int32Array;
  count = 0;
  constructor(capacity: number) {
    this.keys = new Int32Array(capacity);
  }
  clear(): void {
    this.count = 0;
  }
  add(k: number): boolean {
    if (this.count >= this.keys.length) return false;
    this.keys[this.count++] = k;
    return true;
  }
  has(k: number): boolean {
    for (let i = 0; i < this.count; i++) if (this.keys[i] === k) return true;
    return false;
  }
}

const scratchQuad = new Float64Array(8);

/** Inflate a convex quad (TL, TR, BR, BL world corners) by `d` metres along its own edge axes. */
export function inflateQuad(q: Float64Array | number[], d: number, out: Float64Array | number[]): void {
  const e1x0 = (q[2] ?? 0) - (q[0] ?? 0);
  const e1y0 = (q[3] ?? 0) - (q[1] ?? 0);
  const e2x0 = (q[6] ?? 0) - (q[0] ?? 0);
  const e2y0 = (q[7] ?? 0) - (q[1] ?? 0);
  const l1 = Math.hypot(e1x0, e1y0) || 1;
  const l2 = Math.hypot(e2x0, e2y0) || 1;
  const e1x = (e1x0 / l1) * d;
  const e1y = (e1y0 / l1) * d;
  const e2x = (e2x0 / l2) * d;
  const e2y = (e2y0 / l2) * d;
  out[0] = (q[0] ?? 0) - e1x - e2x;
  out[1] = (q[1] ?? 0) - e1y - e2y;
  out[2] = (q[2] ?? 0) + e1x - e2x;
  out[3] = (q[3] ?? 0) + e1y - e2y;
  out[4] = (q[4] ?? 0) + e1x + e2x;
  out[5] = (q[5] ?? 0) + e1y + e2y;
  out[6] = (q[6] ?? 0) - e1x + e2x;
  out[7] = (q[7] ?? 0) - e1y + e2y;
}

/** Separating-axis test: does the world AABB [x0,x1]×[y0,y1] intersect the convex quad? */
export function aabbIntersectsQuad(x0: number, y0: number, x1: number, y1: number, q: Float64Array | number[]): boolean {
  let qminx = Infinity;
  let qmaxx = -Infinity;
  let qminy = Infinity;
  let qmaxy = -Infinity;
  for (let i = 0; i < 4; i++) {
    const x = q[i * 2] ?? 0;
    const y = q[i * 2 + 1] ?? 0;
    if (x < qminx) qminx = x;
    if (x > qmaxx) qmaxx = x;
    if (y < qminy) qminy = y;
    if (y > qmaxy) qmaxy = y;
  }
  if (qmaxx <= x0 || qminx >= x1 || qmaxy <= y0 || qminy >= y1) return false;
  // quad edge normals (two unique for a parallelogram, but test all four for generality)
  for (let e = 0; e < 4; e++) {
    const ax = q[e * 2] ?? 0;
    const ay = q[e * 2 + 1] ?? 0;
    const bx = q[((e + 1) % 4) * 2] ?? 0;
    const by = q[((e + 1) % 4) * 2 + 1] ?? 0;
    const nx = -(by - ay);
    const ny = bx - ax;
    let pmin = Infinity;
    let pmax = -Infinity;
    for (let i = 0; i < 4; i++) {
      const p = (q[i * 2] ?? 0) * nx + (q[i * 2 + 1] ?? 0) * ny;
      if (p < pmin) pmin = p;
      if (p > pmax) pmax = p;
    }
    const c0 = x0 * nx + y0 * ny;
    const c1 = x1 * nx + y0 * ny;
    const c2 = x1 * nx + y1 * ny;
    const c3 = x0 * nx + y1 * ny;
    const tmin = Math.min(c0, c1, c2, c3);
    const tmax = Math.max(c0, c1, c2, c3);
    if (tmax <= pmin || tmin >= pmax) return false;
  }
  return true;
}

function collect(grid: TileGridSpec, li: number, q: Float64Array | number[], out: TileKeySet, skip: TileKeySet | null): void {
  const lv = grid.levels[li];
  if (!lv) return;
  let minx = Infinity;
  let maxx = -Infinity;
  let miny = Infinity;
  let maxy = -Infinity;
  for (let i = 0; i < 4; i++) {
    const x = q[i * 2] ?? 0;
    const y = q[i * 2 + 1] ?? 0;
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  }
  const ox = grid.origin[0];
  const oy = grid.origin[1];
  const tm = lv.tile_m;
  const tx0 = Math.max(0, Math.floor((minx - ox) / tm));
  const tx1 = Math.min(lv.nx - 1, Math.floor((maxx - ox) / tm));
  const ty0 = Math.max(0, Math.floor((miny - oy) / tm));
  const ty1 = Math.min(lv.ny - 1, Math.floor((maxy - oy) / tm));
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const x0 = ox + tx * tm;
      const y0 = oy + ty * tm;
      if (!aabbIntersectsQuad(x0, y0, x0 + tm, y0 + tm, q)) continue;
      const k = tileKey(lv.z, tx, ty);
      if (skip?.has(k)) continue;
      out.add(k);
    }
  }
}

/**
 * Visible tiles of level index `li` for the view quad `quad` (TL, TR, BR, BL world corners of the
 * viewport), plus the prefetch ring: tiles within `ring` tiles of the view (quad inflated by
 * ring·tile_m along its own axes) that are not visible. Allocation-free.
 */
export function visibleTiles(
  grid: TileGridSpec,
  li: number,
  quad: Float64Array | number[],
  visible: TileKeySet,
  prefetch: TileKeySet | null,
  ring = 1,
): void {
  visible.clear();
  collect(grid, li, quad, visible, null);
  if (prefetch) {
    prefetch.clear();
    const lv = grid.levels[li];
    if (lv && ring > 0) {
      inflateQuad(quad, ring * lv.tile_m, scratchQuad);
      collect(grid, li, scratchQuad, prefetch, visible);
    }
  }
}
